import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { credentialsActions } from './slice';

// 🔥 GET ALL CREDENTIALS
export function* getCredentials() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get('/credentials'));

		if (res.statusCode === 200) {
			yield put(credentialsActions.loadCredentialsSuccess({ orgID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load credentials.');
		}
	} catch (error) {
		yield put(credentialsActions.loadCredentialsError(error));
		Notification('error', error.message);
	}
}

// ➕ CREATE CREDENTIAL
export function* createCredential({ payload }) {
	try {
		const { data } = payload;
		const res = yield call(() => SuperFetch.post('/credentials', data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.createCredentialSuccess());
			Notification('success', 'Credential created successfully.');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to create credential.');
		}
	} catch (error) {
		yield put(credentialsActions.createCredentialError(error));
		Notification('error', error.message);
	}
}

export function* createCredentialSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// ✏️ UPDATE CREDENTIAL
export function* updateCredential({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}`, data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.updateCredentialSuccess({ credentialID }));
			Notification('success', 'Credential updated successfully.');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to update credential.');
		}
	} catch (error) {
		yield put(credentialsActions.updateCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* updateCredentialSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// ❌ DELETE CREDENTIAL
export function* deleteCredential({ payload }) {
	const { credentialID } = payload;
	try {
		const res = yield call(() => SuperFetch.delete(`/credentials/${credentialID}`));

		if (res.statusCode === 200) {
			yield put(credentialsActions.deleteCredentialSuccess({ credentialID }));
			Notification('success', 'Credential deleted successfully.');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to delete credential.');
		}
	} catch (error) {
		yield put(credentialsActions.deleteCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* deleteCredentialSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// 🎴 ASSIGN CREDENTIAL
export function* assignCredential({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}/assign`, data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.assignCredentialSuccess({ credentialID }));
			Notification('success', 'Credential assigned successfully.');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to assign credential.');
		}
	} catch (error) {
		yield put(credentialsActions.assignCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* assignCredentialSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// ❌ UNASSIGN CREDENTIAL
export function* unassignCredential({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}/unassign`, data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.unassignCredentialSuccess({ credentialID }));
			Notification('success', 'Credential unassigned successfully.');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to unassign credential.');
		}
	} catch (error) {
		yield put(credentialsActions.unassignCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* unassignCredentialSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// 🔗 ASSOCIATE CARD (card-specific — links CSN cross-integration)
export function* associateCard({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}/associate`, data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.associateCardSuccess({ credentialID }));
			Notification('success', 'Card associated successfully.');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to associate card.');
		}
	} catch (error) {
		yield put(credentialsActions.associateCardError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* associateCardSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// 🔄 GET CREDENTIAL ACCESS
export function* getCredentialAccess() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get('/credentials/access'));

		if (res.statusCode === 200) {
			yield put(credentialsActions.getCredentialAccessSuccess({ orgID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load credential access.');
		}
	} catch (error) {
		yield put(credentialsActions.getCredentialAccessError(error));
	}
}

// 🏢 UPDATE CREDENTIAL SITES
export function* updateCredentialSites({ payload }) {
	try {
		const { credentialID, siteIDs } = payload;
		const res = yield call(() => SuperFetch.put(`/credentials/${credentialID}/sites`, { siteIDs }));

		if (res.statusCode === 200) {
			yield put(credentialsActions.updateCredentialSitesSuccess());
			Notification('success', 'Credential sites updated successfully.');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to update credential sites.');
		}
	} catch (error) {
		yield put(credentialsActions.updateCredentialSitesError(error));
		Notification('error', error.message);
	}
}

export function* updateCredentialSitesSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// 📦 BATCH IR CARDS
export function* createCardsBatchIR({ payload }) {
	try {
		const { data } = payload;
		const res = yield call(() => SuperFetch.post('/credentials/batch-ir', data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.createCardsBatchIRSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to create IR batch cards.');
		}
	} catch (error) {
		yield put(credentialsActions.createCardsBatchIRError(error));
		Notification('error', error.message);
	}
}

export function* createCardsBatchIRSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// 🎫 ADD GUEST (sequential: grant/revoke permissions → assign/update credentials)
export function* addGuest({ payload }) {
	const { personID, addedDevices, removedDevices, addedGroups, removedGroups, credentialUpdates, credentialUnassigns } = payload;
	try {
		// Step 1: Grant new permissions
		if (addedDevices?.length > 0 || addedGroups?.length > 0) {
			const grantPayload = {
				personIDs: [personID],
				...(addedDevices?.length > 0 && { deviceIDs: addedDevices }),
				...(addedGroups?.length > 0 && { assetGroupIDs: addedGroups }),
				permissionTypeID: 1
			};
			const grantRes = yield call(() => SuperFetch.post('/access/grantPermission', grantPayload));
			if (grantRes.statusCode !== 200) {
				throw Error(grantRes.error?.message || grantRes.error || 'Failed to grant permissions.');
			}
		}

		// Step 2: Revoke removed permissions
		if (removedDevices?.length > 0 || removedGroups?.length > 0) {
			const revokePayload = {
				personIDs: [personID],
				...(removedDevices?.length > 0 && { deviceIDs: removedDevices }),
				...(removedGroups?.length > 0 && { assetGroupIDs: removedGroups }),
				permissionTypeID: 1
			};
			const revokeRes = yield call(() => SuperFetch.post('/access/revokePermission', revokePayload));
			if (revokeRes.statusCode !== 200) {
				throw Error(revokeRes.error?.message || revokeRes.error || 'Failed to revoke permissions.');
			}
		}

		// Step 3: Unassign removed credentials
		for (const credentialID of credentialUnassigns || []) {
			const unassignRes = yield call(() =>
				SuperFetch.patch(`/credentials/${credentialID}/unassign`, { oldPersonID: personID })
			);
			if (unassignRes.statusCode !== 200) {
				throw Error(unassignRes.error?.message || unassignRes.error || 'Failed to unassign credential.');
			}
		}

		// Step 4: Assign/update each credential (sequentially)
		for (const cred of credentialUpdates || []) {
			const body = {
				startDate: cred.startDate,
				endDate: cred.endDate,
				...(cred.isNew && { personID })
			};
			const credRes = yield call(() => SuperFetch.patch(`/credentials/${cred.credentialID}`, body));
			if (credRes.statusCode !== 200) {
				throw Error(credRes.error?.message || credRes.error || 'Failed to update credential.');
			}
		}

		yield put(credentialsActions.addGuestSuccess());
		Notification('success', 'Guest configured successfully.');
	} catch (error) {
		yield put(credentialsActions.addGuestError({ error: error.message }));
		Notification('error', error.message);
	}
}

export function* addGuestSuccess() {
	yield put(credentialsActions.loadCredentials());
	yield put(credentialsActions.getCredentialAccess());
}

// 📡 FETCH PROPAGATION (single endpoint — active queue + failed deliveries)
// Rehydrates both Active tab (credentialPropagation) and Pending tab (propagationStatus) on mount/reconnect.
export function* fetchPropagation() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const query = siteID && siteID !== 'all' ? { siteID } : {};
		const res = yield call(() => SuperFetch.get('/credentials/propagation', {}, query));
		if (res.statusCode === 200 && res.data) {
			const { queued, active, completed, failed } = res.data;
			// scope: same org+site merges with live socket progress, a switch replaces
			yield put(
				credentialsActions.hydrateCredentialPropagation({
					queued,
					active,
					completed,
					scope: `${orgID}:${siteID}`
				})
			);
			yield put(credentialsActions.fetchPropagationStatusSuccess(failed || []));
		}
	} catch (error) {
		// Silent — rehydration is best-effort
	}
}

// 📡 RETRY CREDENTIAL PROPAGATION (fire-and-forget — socket progress updates UI)
export function* retryCredentialPropagation({ payload }) {
	try {
		const { credentialID, deviceIDs } = payload;
		const res = yield call(() => SuperFetch.post(`/credentials/${credentialID}/retry`, { deviceIDs }));

		if (res.statusCode !== 200) {
			throw Error(res.error?.message || res.error || 'Failed to retry propagation.');
		}
	} catch (error) {
		Notification('error', error.message);
	}
}

// ─── ROOT SAGA ───────────────────────────────────────────────────────────────

export default function* credentialsSaga() {
	yield takeLatest(credentialsActions.loadCredentials, getCredentials);

	// CRUD
	yield takeLatest(credentialsActions.createCredential, createCredential);
	yield takeLatest(credentialsActions.createCredentialSuccess, createCredentialSuccess);
	yield takeLatest(credentialsActions.updateCredential, updateCredential);
	yield takeLatest(credentialsActions.updateCredentialSuccess, updateCredentialSuccess);
	yield takeLatest(credentialsActions.deleteCredential, deleteCredential);
	yield takeLatest(credentialsActions.deleteCredentialSuccess, deleteCredentialSuccess);

	// Assign / Unassign
	yield takeLatest(credentialsActions.assignCredential, assignCredential);
	yield takeLatest(credentialsActions.assignCredentialSuccess, assignCredentialSuccess);
	yield takeLatest(credentialsActions.unassignCredential, unassignCredential);
	yield takeLatest(credentialsActions.unassignCredentialSuccess, unassignCredentialSuccess);

	// Card-specific
	yield takeLatest(credentialsActions.associateCard, associateCard);
	yield takeLatest(credentialsActions.associateCardSuccess, associateCardSuccess);
	yield takeLatest(credentialsActions.createCardsBatchIR, createCardsBatchIR);
	yield takeLatest(credentialsActions.createCardsBatchIRSuccess, createCardsBatchIRSuccess);

	// Credential Sites
	yield takeLatest(credentialsActions.updateCredentialSites, updateCredentialSites);
	yield takeLatest(credentialsActions.updateCredentialSitesSuccess, updateCredentialSitesSuccess);

	// Access
	yield takeLatest(credentialsActions.getCredentialAccess, getCredentialAccess);

	// Guest
	yield takeLatest(credentialsActions.addGuest, addGuest);
	yield takeLatest(credentialsActions.addGuestSuccess, addGuestSuccess);

	// Propagation retry
	yield takeLatest(credentialsActions.fetchPropagation, fetchPropagation);
	yield takeLatest(credentialsActions.retryCredentialPropagation, retryCredentialPropagation);
}
